import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuthStore from '../stores/useAuthStore';
import useAnalyticsStore from '../stores/useAnalyticsStore';
import CourseCard from '../components/CourseCard';
import DashboardLayout from '../components/DashboardLayout';
import axiosClient from '../services/api';
import { useToast } from '../components/ToastContainer';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    progressOverview,
    courseProgress,
    nextSteps,
    fetchProgressOverview,
    fetchCourseProgress,
    fetchNextSteps,
    isLoading,
    error
  } = useAnalyticsStore();

  const [activeTab, setActiveTab] = useState('overview');

  console.log(progressOverview)

  const [userBadge, setUserBadge] = useState(null);
  const [isLoadingBadge, setIsLoadingBadge] = useState(true);
  const [errorBadge, setErrorBadge] = useState(null);

  const [AssignedInfo, setAssignedInfo] = useState(null);
  const [loadingAssignedInfo, setLoadingAssignedInfo] = useState(true);
  const toast = useToast();

  const [orgEnrollments, setOrgEnrollments] = useState([]);
  const [schoolEnrollments, setSchoolEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState({ org: false, school: false });

  const fetchStudentData = async () => {
    try {
      const res = await axiosClient.get('/courses/my_assigned_courses');
      setAssignedInfo(res.data.organizationPaths);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoadingAssignedInfo(false);
    }
  };

  const fetchOrgEnrollments = async () => {
    setLoadingEnrollments(prev => ({ ...prev, org: true }));
    try {
      const res = await axiosClient.get('/courses/org/my_courses');
      if (res.data.success) {
        setOrgEnrollments(res.data.enrollments);
      } else {
        toast.error(res.data.error || 'Failed to load organization courses');
      }
    } catch (err) {
      toast.error('Failed to load organization courses');
    } finally {
      setLoadingEnrollments(prev => ({ ...prev, org: false }));
    }
  };


  const fetchSchoolEnrollments = async () => {
    setLoadingEnrollments(prev => ({ ...prev, school: true }));
    try {
      const res = await axiosClient.get('/courses/school/my_courses');
      if (res.data.success) {
        setSchoolEnrollments(res.data.enrollments);
      } else {
        toast.error(res.data.error || 'Failed to load school courses');
      }
    } catch (err) {
      toast.error('Failed to load school courses');
    } finally {
      setLoadingEnrollments(prev => ({ ...prev, school: false }));
    }
  };


  const loadData = async () => {
    try {
      setIsLoadingBadge(true);
      const userRes = await axiosClient.get('/badges/me/');
      setUserBadge(userRes.data.user);
    } catch (err) {
      setErrorBadge(err.response?.data?.error || 'Failed to load data');
    } finally {
      setIsLoadingBadge(false);
    }
  };

  useEffect(() => {
    fetchStudentData();
    loadData();
    fetchOrgEnrollments();
    fetchSchoolEnrollments();
  }, []);

  useEffect(() => {
    fetchProgressOverview();
    fetchCourseProgress();
    fetchNextSteps();
  }, [fetchCourseProgress, fetchNextSteps, fetchProgressOverview]);

  const handleContinueLearning = () => {
    if (nextSteps?.action === 'continue_lesson' && nextSteps.lessonId && nextSteps.courseId) {
      navigate(`/student/learning/${nextSteps.courseId}/lesson/${nextSteps.lessonId}`);
    } else if (nextSteps?.action === 'view_course' && nextSteps.courseId) {
      navigate(`/student/learning/${nextSteps.courseId}`);
    } else {
      navigate('/student/my-courses');
    }
  };

  const BadgeDisplay = ({ badges }) => {
    if (!badges || badges?.length === 0) return null;

    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">Achievements</h2>
        <div className="flex flex-wrap gap-4">
          {badges?.map((badge) => (
            <div key={badge._id} className="text-center">
              <img
                src={`${process.env.REACT_APP_SOCKET_URL}/public${badge?.badge?.icon}`}
                alt={badge?.badge?.name}
                className="w-16 h-16 mx-auto mb-2"
              />
              <p className="text-xs font-medium text-neutral-900 dark:text-white">{badge?.badge?.name}</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{badge?.badge?.points} pts</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const allCourseList = [
    ...(courseProgress?.map(cp => ({ ...cp.course, source: 'personal', enrollmentId: cp._id })) || []),
    ...orgEnrollments.map(e => ({ ...e.course, source: 'org', sourceName: e.companyName, enrollmentId: e._id })),
    ...schoolEnrollments.map(e => ({ ...e.course, source: 'school', sourceName: e.schoolName, enrollmentId: e._id }))
  ];

  if (isLoading || isLoadingBadge || loadingAssignedInfo) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || errorBadge) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Error loading dashboard</h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">{error}</p>
          <button
            onClick={() => {
              fetchProgressOverview();
              fetchStudentData();
              fetchCourseProgress();
              loadData();
              fetchNextSteps();
            }}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout userRole={user.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">
              Welcome back, {user?.name?.split(' ')[0] || 'Student'}!
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Track your learning journey and discover what's next
            </p>
          </div>

          {/* Stats Cards */}
          {progressOverview && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">🎓</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Courses</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {progressOverview.totalEnrolledCourses}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">📘 / ✅</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Lessons / Completed</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {progressOverview?.totalLessons} / {progressOverview?.totalCompletedLessons}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">⏱️</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">AVG Percentage</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {progressOverview?.averageProgressPercentage}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-info-100 dark:bg-info-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">🏆</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Certificates</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {progressOverview.certificates.total}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-info-100 dark:bg-info-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">🏅</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Certificates Isued</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {progressOverview.certificates.issued}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-info-100 dark:bg-info-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">🚫</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Certificates Revoked</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {progressOverview.certificates.revoked}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* Action Banner — Continue Learning or Next Steps */}
          {nextSteps && (
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700 rounded-lg shadow-sm p-6 mb-8 text-white">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="mb-4 md:mb-0">
                  <h2 className="text-2xl font-bold mb-2">🚀 {nextSteps.message}</h2>
                  {nextSteps.courseTitle && (
                    <p className="text-primary-100">
                      {nextSteps.lessonTitle
                        ? `Continue with "${nextSteps.lessonTitle}"`
                        : `Start with "${nextSteps.courseTitle}"`
                      }
                    </p>
                  )}
                </div>
                <button
                  onClick={handleContinueLearning}
                  className="px-6 py-3 bg-white text-primary-600 hover:bg-primary-50 font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-500 transition-all duration-200"
                >
                  {nextSteps.action === 'continue_lesson' ? 'Continue Lesson' : 'View Course'}
                </button>
              </div>
            </div>
          )}

          {/* Organization Learning Paths */}
          {AssignedInfo?.organizationPaths && AssignedInfo.organizationPaths.length > 0 && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-8">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">Organization Learning Paths</h2>
              <div className="space-y-4">
                {AssignedInfo.organizationPaths.map((path) => (
                  <div key={path._id} className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-medium text-neutral-900 dark:text-white">{path.pathTitle}</h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                          {path.courseCount} courses • {path.status}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${path.status === 'completed'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : path.status === 'in_progress'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}>
                        {path.status}
                      </span>
                    </div>
                    <div className="mt-3 space-y-2">
                      {path.courses.map((course, i) => (
                        <div key={i} className="flex flex-wrap items-center justify-between gap-2 text-sm p-2 bg-neutral-50 dark:bg-neutral-700/30 rounded">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${course.completed ? 'text-green-600 dark:text-green-400' : 'text-neutral-700 dark:text-neutral-300'}`}>
                              {course.title}
                            </span>
                            {course.isMandatory && (
                              <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs font-semibold rounded-full">
                                Mandatory
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {course.isEnrolled ? (
                              <>
                                <span className={`text-xs ${course.completed ? 'text-green-600 dark:text-green-400' : 'text-neutral-500 dark:text-neutral-400'}`}>
                                  {course.completed ? 'Completed' : `${course.progress}%`}
                                </span>
                                <Link
                                  to={`/student/learning/${course.courseId._id}`}
                                  className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
                                >
                                  Go to Course
                                </Link>
                              </>
                            ) : (
                              <span className="text-xs text-orange-600 dark:text-orange-400 italic">
                                Not Enrolled - Contact Facilitator
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}


          {userBadge?.earnedBadges && <BadgeDisplay badges={userBadge.earnedBadges} />}

          {/* Tabs */}
          <div className="mb-8">
            <div className="border-b border-neutral-200 dark:border-neutral-700">
              <nav className="-mb-px flex space-x-8">
                {['overview', 'courses', 'recommendations'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300 dark:text-neutral-400 dark:hover:text-neutral-300'
                      }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-8">

              {/* Course Completion Rate */}
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Course Completion Rate</h2>
                <div className="space-y-6">
                  {courseProgress?.length > 0 ? (
                    courseProgress?.map((course, index) => (
                      <div key={index} className="p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-medium text-neutral-900 dark:text-white">{course.course?.title}</h3>
                          <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs rounded-full">
                            {course.course?.category?.name}
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="flex items-center mb-2">
                            <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 mr-2">
                              <div
                                className="bg-primary-500 h-2 rounded-full"
                                style={{ width: `${course.courseProgress || 0}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-medium text-neutral-900 dark:text-white ml-2">
                              {course.courseProgress || 0}%
                            </span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-neutral-600 dark:text-neutral-400">
                          <div>
                            <p className="font-medium text-neutral-700 dark:text-neutral-300">Lessons</p>
                            <p> {course.completedLessons?.length || 0} / {course.course.totalLessons || 0} completed</p>
                          </div>
                          <div>
                            <p className="font-medium text-neutral-700 dark:text-neutral-300">Last Accessed</p>
                            <p>{new Date(course.lastAccessed).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-neutral-500 dark:text-neutral-400">No course progress data available.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'courses' && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Your Courses</h2>
              {allCourseList.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {allCourseList.map((course, index) => (
                    <CourseCard
                      key={`${course.source}-${course._id}-${index}`}
                      course={course}
                      isEnrolled={true}
                      enrollmentSource={course.source}
                      sourceName={course.sourceName}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📚</div>
                  <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No enrolled courses</h3>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                    Enroll in your first course to start tracking progress
                  </p>
                  <button
                    onClick={() => navigate('/courses')}
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
                  >
                    Browse Courses
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'recommendations' && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Recommended for You</h2>
              {progressOverview?.recommendations && progressOverview.recommendations?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {progressOverview?.recommendations?.map((course, index) => (
                    <CourseCard
                      key={index}
                      course={course}
                      onEnroll={() => navigate(`/course/${course.slug}/${course._id}`)}
                      isEnrolled={false}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🎯</div>
                  <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No recommendations yet</h3>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                    Complete more courses to get personalized recommendations
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentDashboard;